import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Ship, Package, FileText, CheckSquare, 
  ChevronRight, Calendar, MapPin, Globe, Truck,
  CheckCircle, Clock, XCircle, AlertCircle, Loader2,
  QrCode, Download, Eye, Plus, Link2, ArrowDownToLine, ArrowUpFromLine,
  Mail, Send, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShipmentTimeline = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [qaRecords, setQaRecords] = useState([]);
  const [exportPacks, setExportPacks] = useState([]);
  const [completionStatus, setCompletionStatus] = useState(null);

  useEffect(() => {
    if (id) {
      fetchShipmentData();
      fetchCompletionStatus();
    }
  }, [id]);

  const fetchShipmentData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/eximstack/shipments/${id}`);
      if (!response.ok) throw new Error('Shipment not found');
      const data = await response.json();
      setShipment(data.shipment);
      setDossiers(data.dossiers || []);
      setQaRecords(data.qa_records || []);
      setExportPacks(data.export_packs || []);
    } catch (error) {
      console.error('Error fetching shipment:', error);
      toast({
        title: "Error",
        description: "Failed to load shipment data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletionStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/shipments/${id}/completion`);
      if (response.ok) {
        const data = await response.json();
        setCompletionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching completion status:', error);
    }
  };

  const sendNotification = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/eximstack/shipments/${id}/notify`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        toast({
          title: "Notification Sent!",
          description: "Completion email has been sent to admin",
        });
        fetchCompletionStatus();
      } else {
        throw new Error(data.detail || 'Failed to send notification');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'In Progress': 'bg-blue-100 text-blue-800 border-blue-300',
      'Shipped': 'bg-purple-100 text-purple-800 border-purple-300',
      'Delivered': 'bg-green-100 text-green-800 border-green-300',
      'Completed': 'bg-green-100 text-green-800 border-green-300',
      'Passed': 'bg-green-100 text-green-800 border-green-300',
      'Failed': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
      case 'Delivered':
      case 'Passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'In Progress':
      case 'Shipped':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'Failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateOverallProgress = () => {
    let progress = 0;
    let stages = 0;
    
    // Shipment created
    if (shipment) {
      progress += 1;
      stages += 1;
    }
    
    // Dossier
    stages += 1;
    if (dossiers.length > 0) {
      const completedDossiers = dossiers.filter(d => d.status === 'Completed' || d.status === 'Shipped').length;
      progress += completedDossiers > 0 ? 1 : (dossiers.length > 0 ? 0.5 : 0);
    }
    
    // QA
    stages += 1;
    if (qaRecords.length > 0) {
      const passedQA = qaRecords.filter(q => q.overall_status === 'Passed').length;
      progress += passedQA > 0 ? 1 : (qaRecords.some(q => q.overall_status === 'In Progress') ? 0.5 : 0);
    }
    
    // Export Pack
    stages += 1;
    if (exportPacks.length > 0) {
      const avgCompletion = exportPacks.reduce((sum, p) => sum + (p.completion_percentage || 0), 0) / exportPacks.length;
      progress += avgCompletion / 100;
    }
    
    return Math.round((progress / stages) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#263776]" />
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Ship className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-2xl font-bold text-gray-600 mb-2">Shipment Not Found</h2>
          <p className="text-gray-500 mb-4">The shipment you're looking for doesn't exist.</p>
          <Link to="/admin">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const overallProgress = calculateOverallProgress();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {shipment.shipment_type === 'Import' ? (
                  <ArrowDownToLine className="w-8 h-8 text-blue-600" />
                ) : (
                  <ArrowUpFromLine className="w-8 h-8 text-green-600" />
                )}
                <h1 className="text-3xl font-bold text-[#263776]">Shipment Timeline</h1>
              </div>
              <p className="text-gray-600 font-mono">{shipment.batch_number}</p>
            </div>
            <Badge className={`${getStatusColor(shipment.status)} px-4 py-2 text-lg`}>
              {shipment.status}
            </Badge>
          </div>
        </div>

        {/* Shipment Overview Card */}
        <Card className="mb-8 border-l-4 border-l-[#263776]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[#263776]">
              <Ship className="w-5 h-5" />
              Shipment Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Product</p>
                <p className="font-semibold">{shipment.product_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <Badge variant="outline" className={shipment.shipment_type === 'Import' ? 'text-blue-600' : 'text-green-600'}>
                  {shipment.shipment_type}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Route</p>
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{shipment.origin}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span>{shipment.destination}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Shipment Date</p>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{formatDate(shipment.shipment_date)}</span>
                </div>
              </div>
            </div>
            
            {/* Overall Progress */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Overall Progress</span>
                <span className="text-lg font-bold text-[#263776]">{overallProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all bg-gradient-to-r from-[#263776] to-[#F47D20]"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-blue-500 to-orange-500 hidden md:block" />
          
          {/* Stage 1: Traceability Dossier */}
          <div className="relative mb-8" data-testid="timeline-dossier">
            <div className="md:ml-20">
              <div className="absolute left-5 md:left-5 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                <FileText className="w-3 h-3 text-white" />
              </div>
              
              <Card className={`border-l-4 ${dossiers.length > 0 ? 'border-l-green-500' : 'border-l-gray-300'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <FileText className="w-5 h-5" />
                      Traceability Dossier
                    </CardTitle>
                    {dossiers.length === 0 && (
                      <Link to={`/eximstack/traceability/new?shipment=${id}`}>
                        <Button size="sm" variant="outline" className="text-green-600 border-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Create
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {dossiers.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p>No traceability dossier linked yet</p>
                      <p className="text-sm">Create a dossier to track farm-to-fork details</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dossiers.map((dossier, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(dossier.status)}
                            <div>
                              <p className="font-medium">{dossier.product_name}</p>
                              <p className="text-sm text-gray-500 font-mono">{dossier.batch_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(dossier.status)}>{dossier.status}</Badge>
                            <Link to={`/eximstack/traceability/${dossier.id}`}>
                              <Button size="sm" variant="ghost">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Stage 2: QA Gates */}
          <div className="relative mb-8" data-testid="timeline-qa">
            <div className="md:ml-20">
              <div className="absolute left-5 md:left-5 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <CheckSquare className="w-3 h-3 text-white" />
              </div>
              
              <Card className={`border-l-4 ${qaRecords.length > 0 ? 'border-l-blue-500' : 'border-l-gray-300'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-blue-700">
                      <CheckSquare className="w-5 h-5" />
                      QA Gates
                    </CardTitle>
                    {qaRecords.length === 0 && (
                      <Link to={`/eximstack/qa-gates/new?shipment=${id}`}>
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-600">
                          <Plus className="w-4 h-4 mr-1" /> Create
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {qaRecords.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p>No QA records linked yet</p>
                      <p className="text-sm">Create a QA record to track quality inspections</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {qaRecords.map((record, idx) => {
                        const passedGates = record.gates?.filter(g => g.overall_status === 'Passed').length || 0;
                        const totalGates = record.gates?.length || 0;
                        
                        return (
                          <div key={idx} className="p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(record.overall_status)}
                                <div>
                                  <p className="font-medium">{record.product_name}</p>
                                  <p className="text-sm text-gray-500 font-mono">{record.batch_id}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={getStatusColor(record.overall_status)}>{record.overall_status}</Badge>
                                <Link to={`/eximstack/qa-gates/${record.id}`}>
                                  <Button size="sm" variant="ghost">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </div>
                            </div>
                            
                            {/* Gate Progress */}
                            <div className="mt-3">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Gate Progress</span>
                                <span className="font-medium">{passedGates}/{totalGates} Passed</span>
                              </div>
                              <div className="flex gap-1">
                                {record.gates?.map((gate, gIdx) => (
                                  <div 
                                    key={gIdx}
                                    className={`flex-1 h-2 rounded-full ${
                                      gate.overall_status === 'Passed' ? 'bg-green-500' :
                                      gate.overall_status === 'Failed' ? 'bg-red-500' :
                                      gate.overall_status === 'In Progress' ? 'bg-blue-500' :
                                      'bg-gray-200'
                                    }`}
                                    title={`Gate ${gate.gate_number}: ${gate.overall_status}`}
                                  />
                                ))}
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                {record.gates?.slice(0, 6).map((gate, gIdx) => (
                                  <span key={gIdx}>G{gate.gate_number}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Stage 3: Export Pack */}
          <div className="relative mb-8" data-testid="timeline-export-pack">
            <div className="md:ml-20">
              <div className="absolute left-5 md:left-5 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Package className="w-3 h-3 text-white" />
              </div>
              
              <Card className={`border-l-4 ${exportPacks.length > 0 ? 'border-l-orange-500' : 'border-l-gray-300'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-orange-700">
                      <Package className="w-5 h-5" />
                      Export Pack
                    </CardTitle>
                    {exportPacks.length === 0 && (
                      <Link to={`/eximstack/export-pack/new?shipment=${id}`}>
                        <Button size="sm" variant="outline" className="text-orange-600 border-orange-600">
                          <Plus className="w-4 h-4 mr-1" /> Create
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {exportPacks.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p>No export pack linked yet</p>
                      <p className="text-sm">Create an export pack to manage shipping documents</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {exportPacks.map((pack, idx) => {
                        const requiredDocs = pack.documents?.filter(d => d.is_required) || [];
                        const uploadedDocs = requiredDocs.filter(d => d.status === 'Uploaded' || d.status === 'Verified');
                        
                        return (
                          <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(pack.status)}
                                <div>
                                  <p className="font-medium">{pack.product_name}</p>
                                  <p className="text-sm text-gray-500 font-mono">{pack.batch_id}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-orange-600">{pack.completion_percentage}%</span>
                                <Link to={`/eximstack/export-pack/${pack.id}`}>
                                  <Button size="sm" variant="ghost">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </div>
                            </div>
                            
                            {/* Document Progress */}
                            <div className="mt-3">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Documents</span>
                                <span className="font-medium">{uploadedDocs.length}/{requiredDocs.length} Ready</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full bg-orange-500 transition-all"
                                  style={{ width: `${pack.completion_percentage}%` }}
                                />
                              </div>
                              
                              {/* Document Types */}
                              <div className="mt-2 flex flex-wrap gap-1">
                                {requiredDocs.slice(0, 6).map((doc, dIdx) => (
                                  <Badge 
                                    key={dIdx} 
                                    variant="outline" 
                                    className={`text-xs ${
                                      doc.status === 'Verified' ? 'bg-green-100 text-green-700' :
                                      doc.status === 'Uploaded' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {doc.document_type}
                                  </Badge>
                                ))}
                                {requiredDocs.length > 6 && (
                                  <Badge variant="outline" className="text-xs">+{requiredDocs.length - 6} more</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Final Status */}
          <div className="relative" data-testid="timeline-complete">
            <div className="md:ml-20">
              <div className={`absolute left-5 md:left-5 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                overallProgress === 100 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
              
              <Card className={`border-l-4 ${overallProgress === 100 ? 'border-l-green-500 bg-green-50' : 'border-l-gray-300'}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {overallProgress === 100 ? (
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      ) : (
                        <Clock className="w-8 h-8 text-gray-400" />
                      )}
                      <div>
                        <p className="font-semibold text-lg">
                          {overallProgress === 100 ? 'Shipment Ready!' : 'Shipment In Progress'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {overallProgress === 100 
                            ? 'All documentation complete'
                            : `${100 - overallProgress}% remaining to complete`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {completionStatus?.notified && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-300 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Notified
                        </Badge>
                      )}
                      {overallProgress === 100 && (
                        <>
                          <Badge className="bg-green-600 text-white px-4 py-2">
                            Ready for Dispatch
                          </Badge>
                          <Button 
                            onClick={sendNotification}
                            disabled={sending || completionStatus?.notified}
                            className="bg-[#F47D20] hover:bg-[#e06d10]"
                            data-testid="send-notification-btn"
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            {completionStatus?.notified ? 'Sent' : 'Send Notification'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Completion Checklist */}
                  {completionStatus && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-600 mb-2">Completion Checklist:</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className={`flex items-center gap-1 text-sm ${completionStatus.has_dossier ? 'text-green-600' : 'text-gray-400'}`}>
                          {completionStatus.has_dossier ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                          Dossier
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${completionStatus.has_qa_record ? 'text-green-600' : 'text-gray-400'}`}>
                          {completionStatus.has_qa_record ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                          QA Record
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${completionStatus.all_qa_passed ? 'text-green-600' : 'text-gray-400'}`}>
                          {completionStatus.all_qa_passed ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                          All QA Passed
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${completionStatus.has_export_pack ? 'text-green-600' : 'text-gray-400'}`}>
                          {completionStatus.has_export_pack ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                          Export Pack
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${completionStatus.all_docs_complete ? 'text-green-600' : 'text-gray-400'}`}>
                          {completionStatus.all_docs_complete ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                          Docs Complete
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Notes */}
        {shipment.notes && (
          <Card className="mt-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{shipment.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ShipmentTimeline;
